import { MEMORY_ARCHIVE_LIKE } from "@/lib/leaderMemoryVisibility";
import { supabase } from "@/integrations/supabase/client";
import {
  buildCoachMemorySource,
  coerceCoachLensSource,
  extractCoachTwinSource,
} from "@/lib/behavior/coachPersonalizationSources";
import type {
  CoachLensSource,
  CoachMemorySource,
  CoachTwinSource,
} from "@/lib/coachSessionReviewContract";

export interface ActiveCoachRuntimeContext {
  coachLens: CoachLensSource | null;
  coachTwin: CoachTwinSource | null;
  coachMemory: CoachMemorySource | null;
}

export async function fetchActiveCoachRuntimeContext(params: {
  coachId: string;
  leaderId?: string | null;
}): Promise<ActiveCoachRuntimeContext> {
  const [coachLensResult, coachProfileResult, coachTwinResult, coachMemoryResult] = await Promise.all([
    (supabase as unknown as { from: typeof supabase.from })
      .from("coach_lens")
      .select("lens_name, preferred_language, avoid_language, frameworks, coach_lens_json, updated_at")
      .eq("coach_id", params.coachId)
      .eq("is_active", true)
      .maybeSingle(),
    (supabase as unknown as { from: typeof supabase.from })
      .from("coach_profiles")
      .select("coaching_philosophy, frameworks")
      .eq("coach_id", params.coachId)
      .maybeSingle(),
    (supabase as unknown as { from: typeof supabase.from })
      .from("coach_twin_profiles")
      .select("version, twin_json")
      .eq("coach_id", params.coachId)
      .eq("status", "active")
      .order("version", { ascending: false })
      .limit(1),
    params.leaderId
      ? (supabase as unknown as { from: typeof supabase.from })
        .from("coach_memory_nodes")
        .select("node_type, title, content")
        .not("node_type", "like", MEMORY_ARCHIVE_LIKE)
        .eq("coach_id", params.coachId)
        .eq("leader_id", params.leaderId)
        .order("created_at", { ascending: false })
        .limit(12)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (coachMemoryResult?.error) console.warn("[coachRuntimeContext] coach_memory_query_failed");
  const coachProfile = coachProfileResult && "data" in coachProfileResult
    ? (coachProfileResult.data as { coaching_philosophy?: string | null; frameworks?: string | null } | null)
    : null;
  const rawCoachLens = coachLensResult?.error
    ? null
    : coerceCoachLensSource((coachLensResult?.data || null) as CoachLensSource | null);
  const coachLensUpdatedAt = (coachLensResult?.data as { updated_at?: string | null } | null)?.updated_at ?? null;
  const coachLens = rawCoachLens
    ? {
        ...rawCoachLens,
        updatedAt: coachLensUpdatedAt,
        coach_lens_json: {
          ...(rawCoachLens.coach_lens_json || {}),
          philosophy: rawCoachLens.coach_lens_json?.philosophy || coachProfile?.coaching_philosophy || null,
          frameworks_raw: rawCoachLens.coach_lens_json?.frameworks_raw || coachProfile?.frameworks || null,
        },
      }
    : coachProfile?.coaching_philosophy || coachProfile?.frameworks
      ? {
          lens_name: null,
          preferred_language: [],
          avoid_language: [],
          frameworks: [],
          coach_lens_json: {
            philosophy: coachProfile?.coaching_philosophy || null,
            frameworks_raw: coachProfile?.frameworks || null,
          },
        }
      : null;

  const twinJson = coachTwinResult && "data" in coachTwinResult
    ? Array.isArray(coachTwinResult.data)
      ? (coachTwinResult.data[0] as { twin_json?: unknown } | undefined)?.twin_json
      : null
    : null;
  const coachTwin = extractCoachTwinSource(twinJson);

  const memoryRows = coachMemoryResult && "data" in coachMemoryResult
    ? (coachMemoryResult.data || []) as Array<{ node_type?: string | null; title?: string | null; content?: string | null }>
    : [];
  const coachMemory = buildCoachMemorySource(memoryRows);

  return {
    coachLens,
    coachTwin,
    coachMemory,
  };
}
